import { Intersection } from '@api/api/ontology';
import { ThemeSchemaResponse } from '@api/api/theme-overview';
import { mapEntity } from '@api/mappers/mapEntity';
import { getIntersectionEdges } from '@api/utils/ontology-utils';
import { Button } from '@design/components/button/Button';
import { EntityCell } from '@terminal/components/entity-cell/EntityCell';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { NonLinearStepper } from '@terminal/components/non-linear-stepper/NonLinearStepper';
import { OneDayReturnCell } from '@terminal/components/one-day-return-cell/OneDayReturnCell';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { useTheme } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { SkeletonLoader } from '@toggle/design-system/src/components/skeleton-loader/SkeletonLoader';
import {
  RevampTable,
  TableCell,
  TableHeader,
} from '@toggle/design-system/src/components/table';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './FinancialMacroThemeToCompanies.module.scss';

interface FinancialMacroThemeToCompaniesProps {
  theme: ThemeSchemaResponse;
  intersections?: Intersection[];
}

export const FinancialMacroThemeToCompanies = ({
  theme,
  intersections,
}: FinancialMacroThemeToCompaniesProps) => {
  const { t } = useTranslation(['widget']);
  const { navigateToTheme } = useNavigateToThemeOverview();
  const { navigateToPage } = useNavigateToAssetOverview();
  const { params } = useQueryParams();
  const industryThemeId = params[queryKeys.industryThemeId] ?? '';
  const { data: themeData, isLoading: isLoadingTheme } = useTheme({
    themeId: industryThemeId,
  });

  const intersection = intersections?.find(
    intersection => intersection.node === `theme/${industryThemeId}`
  );
  const edgesToDisplay = intersection
    ? getIntersectionEdges(intersection).slice(0, 2)
    : [];

  const { data: entitiesData, isLoading: isLoadingEntities } = useEntities({
    entityTags: edgesToDisplay.map(edge => edge.target.split('/')[1]) ?? [],
  });

  const openThemeOverview = () => {
    if (themeData) {
      const searchParams = new URLSearchParams();
      searchParams.set(queryKeys.activeSubTab, 'companies');
      navigateToTheme({
        themeId: themeData.id,
        source: POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW,
        searchParams,
      });
    }
  };

  const isMacro = theme.type === 'macro';
  const tableData = useMemo(() => {
    return (
      edgesToDisplay.map(edge => {
        return {
          entityTag: edge.target.split('/')[1],
          description: edge.attributes.description,
          entity: entitiesData?.find(
            entity => entity.tag === edge.target.split('/')[1]
          ),
        };
      }) ?? []
    );
  }, [edgesToDisplay, entitiesData]);

  if (isLoadingTheme || isLoadingEntities) {
    return (
      <div className={styles.container}>
        <SkeletonLoader
          areas={[
            {
              styles: { width: '100%', height: '200px', borderRadius: '8px' },
            },
          ]}
        />
      </div>
    );
  }

  if (!themeData || !intersection) {
    throw new Error('Theme or intersection not found');
  }

  return (
    <div className={styles.container}>
      <ErrorBoundaryWrapper resetKeys={[theme.id]}>
        <h2 className={styles.title}>
          {t('widget:themeOverview.drawer.themeExposureToTheme', {
            themeName: theme.name,
            targetThemeName: themeData.name,
          })}
        </h2>
        <div className={styles.stepperWrapper}>
          <NonLinearStepper
            steps={[
              {
                logoUrl: theme.logo_url,
                hoverLabel: theme.name,
                connectorLabel: t('widget:themeOverview.drawer.exposedTo'),
                iconColor: isMacro
                  ? 'var(--icon-decorative-teal)'
                  : 'var(--icon-decorative-purple)',
              },
              {
                logoUrl: themeData?.logo_url,
                hoverLabel: themeData?.name,
                iconColor: 'var(--kg-theme)',
              },
            ]}
            activeIndex={0}
            iconSize={23}
          />
        </div>
        <p className={styles.description}>
          {intersection.relationship_description}
        </p>
        <div className={styles.companiesWrapper}>
          <div className={styles.companiesHeader}>
            <div className={styles.companiesTitle}>
              {t('widget:themeOverview.drawer.topCompaniesTitle', {
                total: tableData.length,
              })}
            </div>
            <Button
              label={t('widget:themeOverview.drawer.viewAllCompanies')}
              iconName="ChevronBoldRight"
              variant="empty"
              size="small"
              iconPosition="right"
              onClick={openThemeOverview}
            />
          </div>

          <RevampTable>
            <thead>
              <tr>
                <TableHeader className={styles.tableHeader} alignment="left">
                  {t('widget:themeOverview.drawer.company')}
                </TableHeader>
                <TableHeader className={styles.tableHeader} alignment="left">
                  {t('widget:themeOverview.drawer.exposureToTheme', {
                    themeName: theme.name,
                  })}
                </TableHeader>
                <TableHeader className={styles.tableHeader} alignment="left">
                  {t('widget:themeOverview.drawer.return1D')}
                </TableHeader>
              </tr>
            </thead>
            <tbody>
              {tableData.map(data => {
                return (
                  <tr
                    key={data.entityTag}
                    onClick={() =>
                      navigateToPage({
                        entityTag: data.entityTag,
                        source: POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW_DRAWER,
                      })
                    }
                  >
                    <TableCell
                      className={clsx(styles.tableCell, styles.companyCell)}
                      alignment="left"
                    >
                      <EntityCell entityTag={data.entityTag} />
                    </TableCell>
                    <TableCell
                      className={clsx(styles.tableCell, styles.descriptionCell)}
                      alignment="left"
                      isNumber={false}
                    >
                      {data.description}
                    </TableCell>
                    <TableCell className={styles.tableCell} alignment="left">
                      <OneDayReturnCell
                        asset={data.entity ? mapEntity(data.entity) : undefined}
                        showPercentageSuffix
                      />
                    </TableCell>
                  </tr>
                );
              })}
            </tbody>
          </RevampTable>
        </div>
      </ErrorBoundaryWrapper>
    </div>
  );
};
